import { QueryClientProvider } from '@tanstack/react-query';
import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DockviewApi } from 'dockview-react';
import { createQueryClient } from './api/query-client.js';
import { WorkspaceShell } from './workspace/shell.js';
import { CommandPalette } from './workspace/command-palette.js';
import { StatusBar } from './workspace/status-bar.js';
import { HotkeyProvider } from './workspace/hotkeys.js';
import { TopBar } from './workspace/top-bar.js';
import { TopBarTabs } from './workspace/top-bar-tabs.js';
import { ProvenanceModal } from './workspace/provenance-modal.js';
import { DockviewApiProvider } from './workspace/dockview-context.js';
import { useAppStore, useTabOrder } from './workspace/store.js';
import { startCrossWindowSync } from './workspace/cross-window-sync.js';
import { BackendSync } from './workspace/backend-sync.js';
import { useCurrentUser } from './use-current-user.js';
import { useFxRatesSync } from './api/use-fx-rates.js';
import { useAlerts } from './api/hooks.js';
import { useOpenAlerts } from './workspace/use-open-alerts.js';
import { FeedbackModal } from './workspace/feedback-modal.js';
import { installDiagnosticListeners } from './diagnostics.js';

// Module-level client so it survives HMR reloads — recreating per render
// drops the cache on every state change.
const queryClient = createQueryClient();

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={<div style={{ padding: 'var(--space-4)' }}>Loading…</div>}>
        <AppShell />
      </Suspense>
    </QueryClientProvider>
  );
}

function AppShell() {
  const { t } = useTranslation();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const userInfo = useCurrentUser();
  // Overlay live latest-spot FX onto the display-currency converters.
  useFxRatesSync();

  // Cross-window state sync: a second hequity tab in the same browser
  // joins the same selection / watchlist / tab state as this one.
  useEffect(() => startCrossWindowSync(), []);
  useEffect(() => installDiagnosticListeners(), []);
  const addTab = useAppStore((s) => s.actions.addTab);
  // Hoisted to AppShell so both the workspace canvas (writer, via the
  // dockview onReady inside TabCanvas) and the command palette (reader,
  // for row→addPanel) share the same handle to the active dockview.
  const dockviewApiRef = useRef<DockviewApi | null>(null);
  const saveWorkspacePrompt = () => {
    const name = window.prompt('Save current layout as…');
    if (!name || name.trim() === '') return;
    addTab({ title: name.trim() });
  };

  return (
    <DockviewApiProvider value={dockviewApiRef}>
      <div
        style={{
          display: 'grid',
          gridTemplateRows: 'auto auto auto 1fr auto',
          height: '100vh',
          width: '100vw',
        }}
      >
        {/* Decorative masthead band — FT-style diagonal pinstripe.
            Purely visual: no interaction, no semantic role. Sits above
            the workspace top bar to give the app an editorial-print
            "masthead" frame. Hatching kept faint so the masthead reads
            as part of the lighter chrome layer, not as a darker bar. */}
        <div
          aria-hidden="true"
          style={{
            height: 12,
            backgroundImage:
              'repeating-linear-gradient(-45deg, rgba(13,17,23,0.07) 0, rgba(13,17,23,0.07) 1px, transparent 1px, transparent 4px)',
            backgroundColor: 'var(--bg)',
            borderBottom: '1px solid var(--hairline)',
          }}
        />
        <AppTopBar onOpenPalette={() => setPaletteOpen(true)} />
        <nav
          aria-label="Workspace tabs"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '4px var(--s-3)',
            borderBottom: '1px solid var(--hairline)',
            // Same cream chrome as the workspace top bar above —
            // continues the lighter header surface down through the
            // tab strip before handing off to the darker canvas.
            background: 'var(--surface)',
          }}
        >
          <TopBarTabs />
        </nav>
        <main
          className="canvas"
          style={{
            minHeight: 0,
            // overflow VISIBLE (not hidden) so popped-out floating panels can
            // render in front of the workspace tabs row above. Docked content
            // is still clipped by dockview's own .dv-split-view-container
            // (overflow:hidden), so nothing else escapes — only the floating
            // overlay host, which sits over the nav as it should. The nav comes
            // before <main> in the DOM, so floats paint in front automatically.
            overflow: 'visible',
            // Darker canvas layer — the workspace area between/behind
            // panels uses --surface-elevated so panel cards sit on a
            // darker ground (FT-style page bg vs lighter chrome). Panel
            // bodies stay --surface (cream) so the cards still pop.
            background: 'var(--surface-elevated)',
          }}
        >
          <WorkspaceShell />
        </main>
        <StatusBar onOpenFeedback={() => setFeedbackOpen(true)} />
        <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
        <HotkeyProvider
          onOpenPalette={() => setPaletteOpen(true)}
          onSaveWorkspace={saveWorkspacePrompt}
        />
        <ProvenanceModal />
        <FeedbackModal
          open={feedbackOpen}
          onOpenChange={setFeedbackOpen}
          userDisplayName={userInfo.displayName}
          userEmail={userInfo.email}
        />
        <BackendSync />
        {/* Force translation provider to evaluate so loading state resolves */}
        <span style={{ display: 'none' }}>{t('app.title')}</span>
      </div>
    </DockviewApiProvider>
  );
}

// Bridges the presentational TopBar to the Zustand workspace store.
// Tabs in the store ARE the workspaces — each one carries its own
// dockview layout + selection state. Switching / renaming / deleting
// is owned by the inline TopBarTabs component now; this bridge just
// surfaces the help-tab id and the current user.
function AppTopBar({ onOpenPalette }: { onOpenPalette: () => void }) {
  const tabOrder = useTabOrder();
  const tabsById = useAppStore((s) => s.tabs);
  const setActiveTab = useAppStore((s) => s.actions.setActiveTab);
  const userInfo = useCurrentUser();
  const openAlerts = useOpenAlerts();
  const { data: alertsData } = useAlerts();
  // Find the Help tab id so the ? icon switches to it.
  const helpTabId = useMemo(
    () => tabOrder.find((id) => tabsById[id]?.title.toLowerCase().startsWith('help')),
    [tabOrder, tabsById],
  );

  return (
    <TopBar
      onOpenPalette={onOpenPalette}
      userDisplayName={userInfo.displayName}
      userEmail={userInfo.email}
      userFirm="Corryk"
      onSignOut={userInfo.signOut}
      onOpenHelp={helpTabId ? () => setActiveTab(helpTabId) : undefined}
      onOpenNotifications={openAlerts}
      notificationCount={alertsData?.unread ?? 0}
    />
  );
}
